import actionTypes from 'constants/actionTypes'
import defaultsDeep from 'lodash/defaultsDeep'
import { isHardFail } from 'lib/action'

type KindData = {
  label: string
  styles: Record<string, unknown>
}

type KindState = {
  id: string
  data: KindData
  syncData: KindData | null
  local: boolean
  isFetching: boolean
  deleted: boolean
  failed: boolean
  modified: number | null
}

type KindAction = {
  type: string
  payload?: {
    label?: string
    styles?: Record<string, unknown>
    data?: {
      label?: string
      styles?: Record<string, unknown>
      modified?: string | number | Date
    }
  }
  meta?: {
    isRetry?: boolean
  }
  error?: {
    status?: number
    response?: {
      status?: number
    }
  }
}

type KindRef = {
  id: string
}

const initialState: KindState = {
  id: '',
  data: {
    label: '',
    styles: {}
  },
  syncData: null,
  local: false,
  isFetching: false,
  deleted: false,
  failed: false,
  modified: null
}

const single = (state: KindState = initialState, action: KindAction, kind: KindRef): KindState => {
  const currentState = {
    ...state,
    id: kind.id
  }
  switch (action.type) {
    case actionTypes.KINDS.ONE.CREATE.REQUEST: {
      let data: Partial<KindData> = {}
      if (action.payload?.label != null) data.label = action.payload.label
      if (action.payload?.styles != null) data.styles = action.payload.styles
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          local: true,
          isFetching: true,
          modified: Date.now()
        },
        currentState
      )
    }
    case actionTypes.KINDS.ONE.CREATE.RECEIVE:
    case actionTypes.KINDS.ONE.CREATE.NOTIFY: {
      const data = defaultsDeep(
        {
          label: action.payload?.data?.label,
          styles: action.payload?.data?.styles
        },
        currentState.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          local: false,
          isFetching: false,
          deleted: false,
          failed: false,
          modified: new Date(action.payload?.data?.modified || Date.now()).getTime() || Date.now()
        },
        currentState
      )
    }
    case actionTypes.KINDS.ONE.CREATE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        currentState
      )
      if (isHardFail(action)) {
        failedState.data = currentState.syncData || currentState.data
        failedState.failed = false
      }
      return failedState
    }

    case actionTypes.KINDS.ONE.EDIT.REQUEST: {
      let data: Partial<KindData> = {}
      if (action.payload?.label != null) data.label = action.payload.label
      if (action.payload?.styles != null) data.styles = action.payload.styles
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          isFetching: true
        },
        currentState
      )
    }
    case actionTypes.KINDS.ONE.EDIT.RECEIVE:
    case actionTypes.KINDS.ONE.EDIT.NOTIFY: {
      const data = defaultsDeep(
        {
          label: action.payload?.data?.label,
          styles: action.payload?.data?.styles
        },
        currentState.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          isFetching: false,
          failed: false,
          modified: new Date(action.payload?.data?.modified || Date.now()).getTime() || Date.now()
        },
        currentState
      )
    }
    case actionTypes.KINDS.ONE.EDIT.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        currentState
      )
      if (isHardFail(action)) {
        failedState.data = currentState.syncData || currentState.data
        failedState.failed = false
      }
      return failedState
    }

    case actionTypes.KINDS.ONE.DELETE.REQUEST:
      return defaultsDeep(
        {
          isFetching: true,
          deleted: true
        },
        currentState
      )
    case actionTypes.KINDS.ONE.DELETE.RECEIVE:
    case actionTypes.KINDS.ONE.DELETE.NOTIFY: {
      const update: Partial<KindState> = { isFetching: false }
      if (!currentState.local) update.deleted = true
      update.modified = Date.now()
      if (action.payload?.data?.modified) update.modified = new Date(action.payload.data.modified).getTime()
      return defaultsDeep(update, currentState)
    }
    case actionTypes.KINDS.ONE.DELETE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        currentState
      )
      if (isHardFail(action)) {
        failedState.deleted = false
        failedState.failed = false
      }
      return failedState
    }

    default:
      return currentState
  }
}

export default single
